import { AuthorizationForm, AuthorizationCallbacks } from '@/features/authorization/authorization-form/authorization-form';
import { Callbacks, Form } from '@/shared/form/form';
import { classInstance } from '@/shared/helpers/helpers';

/** @inheritdoc */
export interface RestorePasswordCallbacks extends Callbacks {
	/**
	 * Коллбэк, выполняющийся при нажатии на «Выслать код повторно». Возвращает Promise.
	 * @category 3 Form
	 * @return {Promise<CallbackResult>} Отдаёт объект с успешным статусом и, опционально, дополнительными данными
	 * @throws {Promise<CallbackError>} Отдаёт объект с неудачным статусом, списком ошибок и, опционально, дополнительными данными
	 */
	onResend?: () => Promise<object>;
}

export class RestorePassword extends AuthorizationForm {
	private readonly modalSelector: string;
	private modal: HTMLElement;
	private form: Form;
	private resendButton: HTMLButtonElement;

	constructor(element: HTMLFormElement, modalSelector: string, callbacks?: AuthorizationCallbacks) {
		super(element, callbacks);
		if (!element) return;
		this.modalSelector = modalSelector;
		this.init();
	}

	/**
	 * @desc Добавление коллбэков после создания формы.
	 * @param {Callbacks} callbacks объект типа Callbacks.
	 * @example
	 * const myForm = app.classInstance.get(document.querySelector('.js-restore-password'));
	 * myForm.restorePassword.addCallbacks({ onSubmit });
	 */
	public addCallbacks = (callbacks: Callbacks): void => {
		this.callbacks = { ...this.callbacks, ...callbacks };
		this.form.addCallbacks(this.callbacks);
	};
	
	/**
	 * Обработчик успешной отправки. Показывает модалку с кнопкой «Отправить код повторно»
	 */
	private handleFormSubmit = (): void => {
		super.handleFormSubmit();
		window.app.modal.open(this.modalSelector);
	};

	/**
	 * Обработчик нажатия на кнопку «Отправить код повторно»
	 */
	private handleResendButtonClick = () => {
		if (this.callbacks.onResend) {
			this.callbacks
				.onResend()
				.then((result) => result.json())
				.then((result) => console.log(result));
		}
	};
	
	/**
	 * Инициализация экземпляра:
	 * * элементы, не создаваемые напрямую из аргументов конструктора;
	 * * обработчики событий.
	 */
	public init = (): void => {
		super.init();
		this.element.addEventListener('formSubmit', this.handleFormSubmit);
		this.modal = document?.querySelector(this.modalSelector);
		this.resendButton = this.modal?.querySelector('#password-restore-resend');
		this.resendButton?.addEventListener('click', this.handleResendButtonClick);
		this.form = classInstance.get(this.element).form;
		classInstance.set(this.element, { restorePassword: this });
	};

	/**
	 * Уничтожение экземпляра:
	 * * обнуление элементов;
	 * * удаление обработчиков.
	*/
	public destroy = (): void => {
		super.destroy();
		this.element?.removeEventListener('formSubmit', this.handleFormSubmit);
		this.resendButton?.removeEventListener('click', this.handleResendButtonClick);
		this.modal = null;
		this.resendButton = null;
		this.form = classInstance.get(this.element).form;
		classInstance.del(this.element, 'restorePassword');
	};
}
